// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Market Inventory Manager
 * 
 * Manages shop inventory with time-based replenishment and overstock mechanics.
 * Each location has limited stock that replenishes over time.
 */

import tradeGoods from '../../config/TradeGoodsConfig';

const STORAGE_KEY = 'marketInventory';
const REPLENISH_RATE = 1; // Units per minute
const DEMAND_KEY = 'marketDemand';
const DEMAND_DECAY = 0.95; // Demand decays 5% per transaction

// Stock limits by location type
const STOCK_LIMITS = {
  station: { min: 100, max: 200 },
  planet: { min: 50, max: 100 },
  remote: { min: 20, max: 50 },
  default: { min: 50, max: 100 }
};

/**
 * Get location type for stock calculation
 */
const getLocationType = (location) => {
  if (location.type === 'station') return 'station';
  if (location.distance > 300) return 'remote';
  return 'planet';
};

/**
 * Get max stock for a location (helper function)
 */
const calculateMaxStock = (location) => {
  const type = getLocationType(location);
  const limits = STOCK_LIMITS[type] || STOCK_LIMITS.default;
  // Random value between min and max for variety
  return Math.floor(limits.min + Math.random() * (limits.max - limits.min));
};

/**
 * Load market inventory from localStorage
 */
const loadInventory = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch (error) {
    console.error('[MarketInventory] Error loading inventory:', error);
    return {};
  }
};

/**
 * Save market inventory to localStorage
 */
const saveInventory = (inventory) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(inventory));
  } catch (error) {
    console.error('[MarketInventory] Error saving inventory:', error);
  }
};

/**
 * Initialize inventory for a location if not exists
 */
const initializeLocation = (locationId, location) => {
  const inventory = loadInventory();
  
  if (!inventory[locationId]) {
    const maxStock = calculateMaxStock(location);
    const goods = {};
    
    // Initialize all trade goods at max stock
    Object.keys(tradeGoods).forEach(goodId => {
      goods[goodId] = {
        current: maxStock,
        max: maxStock,
        lastUpdate: Date.now()
      };
    });
    
    inventory[locationId] = {
      goods,
      lastVisit: Date.now()
    };
    
    saveInventory(inventory);
  }
  
  return inventory[locationId];
};

/**
 * Calculate replenishment based on time elapsed
 */
const calculateReplenishment = (currentStock, maxStock, minutesElapsed) => {
  const replenishAmount = Math.floor(minutesElapsed * REPLENISH_RATE);
  
  if (currentStock < maxStock) {
    // Replenish up to max
    return Math.min(maxStock, currentStock + replenishAmount);
  } else if (currentStock > maxStock) {
    // Reduce overstock down to max
    return Math.max(maxStock, currentStock - replenishAmount);
  }
  
  return currentStock;
};

/**
 * Get current stock for a good at a location (with time-based replenishment)
 */
export const getStock = (locationId, goodId, location) => {
  const inventory = loadInventory();
  const locationData = inventory[locationId] || initializeLocation(locationId, location);
  const goodData = locationData.goods[goodId];
  
  if (!goodData) {
    // Initialize this good if missing
    const maxStock = calculateMaxStock(location);
    locationData.goods[goodId] = {
      current: maxStock,
      max: maxStock,
      lastUpdate: Date.now()
    };
    saveInventory(inventory);
    return maxStock;
  }
  
  // Calculate time elapsed since last update
  const now = Date.now();
  const minutesElapsed = (now - goodData.lastUpdate) / (1000 * 60);
  
  // Calculate replenished stock
  const replenishedStock = calculateReplenishment(
    goodData.current,
    goodData.max,
    minutesElapsed
  );
  
  return replenishedStock;
};

/**
 * Get max stock for a good at a location
 */
export const getMaxStock = (locationId, goodId, location) => {
  const inventory = loadInventory();
  const locationData = inventory[locationId] || initializeLocation(locationId, location);
  const goodData = locationData.goods[goodId];
  
  return goodData ? goodData.max : calculateMaxStock(location);
};

/**
 * Update stock after a transaction
 */
export const updateStock = (locationId, goodId, quantityChange, location) => {
  const inventory = loadInventory();
  const locationData = inventory[locationId] || initializeLocation(locationId, location);
  
  // Get current stock with replenishment
  const currentStock = getStock(locationId, goodId, location);
  const newStock = currentStock + quantityChange;
  
  // Update stock (can go negative for buying, or above max for selling)
  locationData.goods[goodId] = {
    ...locationData.goods[goodId],
    current: Math.max(0, newStock), // Never go below 0
    lastUpdate: Date.now()
  };
  
  locationData.lastVisit = Date.now();
  inventory[locationId] = locationData;
  saveInventory(inventory);
  
  return locationData.goods[goodId].current;
};

/**
 * Get stock level percentage (for price modifiers)
 */
export const getStockLevel = (locationId, goodId, location) => {
  const current = getStock(locationId, goodId, location);
  const max = getMaxStock(locationId, goodId, location);
  
  return (current / max) * 100;
};

/**
 * Get price modifier based on stock level
 */
export const getStockPriceModifier = (stockLevel) => {
  if (stockLevel === 0) return 1.30; // +30% when empty
  if (stockLevel <= 25) return 1.15; // +15% when low
  if (stockLevel <= 75) return 1.0; // Normal price
  if (stockLevel <= 100) return 0.90; // -10% when high stock
  if (stockLevel <= 150) return 0.75; // -25% when overstocked
  return 0.60; // -40% when flooded
};

/**
 * Get all stock data for a location (for UI display)
 */
export const getLocationInventory = (locationId, location) => {
  const inventory = loadInventory();
  // Initialize location if needed
  if (!inventory[locationId]) {
    initializeLocation(locationId, location);
  }
  
  const result = {};
  Object.keys(tradeGoods).forEach(goodId => {
    const current = getStock(locationId, goodId, location);
    const max = getMaxStock(locationId, goodId, location);
    const level = getStockLevel(locationId, goodId, location);
    
    result[goodId] = {
      current,
      max,
      level,
      priceModifier: getStockPriceModifier(level)
    };
  });
  
  return result;
};

/**
 * Reset all market inventory (for game reset)
 */
export const resetMarketInventory = () => {
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Get stock status label for UI
 */
export const getStockStatus = (stockLevel) => {
  if (stockLevel === 0) return { label: 'OUT OF STOCK', color: '#ff4444' };
  if (stockLevel <= 25) return { label: 'LOW STOCK', color: '#ffaa00' };
  if (stockLevel <= 75) return { label: 'IN STOCK', color: '#00ffff' };
  if (stockLevel <= 100) return { label: 'WELL STOCKED', color: '#00ff88' };
  if (stockLevel <= 150) return { label: 'OVERSTOCKED', color: '#ffaa00' };
  return { label: 'FLOODED', color: '#ff4444' };
};


// ============================================================================
// DYNAMIC DEMAND TRACKING
// ============================================================================

const loadDemand = () => {
  try { return JSON.parse(localStorage.getItem(DEMAND_KEY) || '{}'); }
  catch { return {}; }
};

const saveDemand = (data) => {
  localStorage.setItem(DEMAND_KEY, JSON.stringify(data));
};

/**
 * Record a trade and update global demand.
 * Buying increases demand (positive), selling decreases it (negative).
 * @param {string} goodId
 * @param {number} quantity - positive for buy, negative for sell
 */
export const recordTrade = (goodId, quantity) => {
  const demand = loadDemand();
  // Decay existing demand toward zero
  Object.keys(demand).forEach(id => {
    demand[id] = Math.round(demand[id] * DEMAND_DECAY);
    if (Math.abs(demand[id]) < 1) delete demand[id];
  });
  demand[goodId] = (demand[goodId] || 0) + quantity;
  saveDemand(demand);
};

/**
 * Get global demand price modifier for a good.
 * High demand = higher prices. Oversupply = lower prices.
 * Clamped to ±20% effect.
 * @param {string} goodId
 * @returns {number} Multiplier (0.80 to 1.20)
 */
export const getDemandModifier = (goodId) => {
  const demand = loadDemand();
  const d = demand[goodId] || 0;
  // Every 10 net units of demand = ~2% price shift, capped at ±20%
  const shift = Math.max(-0.20, Math.min(0.20, d * 0.002));
  return 1 + shift;
};

const MarketInventoryManager = {
  getStock,
  getMaxStock,
  updateStock,
  getStockLevel,
  getStockPriceModifier,
  getLocationInventory,
  resetMarketInventory,
  recordTrade,
  getDemandModifier,
  getStockStatus
};

export default MarketInventoryManager;
